import type { MouseEvent } from 'react';
import { useState } from 'react';
import { IconButton, Tooltip } from '@mui/material';
import { LibraryAddOutlined, LibraryAddCheck } from '@mui/icons-material';
import { useUpdateSessionMutation } from '@/state/mutations/sessions/use-update-session.ts';
import { useTranslations } from '@/i18n/use-translations.tsx';
import { useNotifications } from '@/hooks/use-notifications';
import type { SessionDto } from '@/types/dtos/sessions/session.dto.ts';
import ConfirmationModal from '@/components/common/confirmation-modal.tsx';
import { useEventStore } from '@/state/stores/use-event-store';
import { SESSION_USAGE_ENTITIES } from '@/types/dtos/sessions/request/session-usage-entities.enum';
import useEventMode from '@/components/events/hooks/use-event-mode';
import { isOrganizeMode } from '@/components/events/helpers/mode.helper';

export const SessionToggleLibrary = ({
  session,
  indicativeOnly = false,
}: {
  session: SessionDto;
  indicativeOnly?: boolean;
}) => {
  const t = useTranslations();
  const notification = useNotifications();
  const [isLibraryModalOpen, setIsLibraryModalOpen] = useState(false);
  const multiFlowEvent = useEventStore((s) => s.event?.multiFlow);
  const { mutateAsync: updateSessionMutation, isPending: isUpdatingSession } = useUpdateSessionMutation();
  const mode = useEventMode();

  const isUsedInMultipleEntities =
    (session.usedInEntities?.filter((entity) => entity.entityType === SESSION_USAGE_ENTITIES.FLOW).length || 0) > 1;

  const handleLibraryIconClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    if (indicativeOnly || isOrganizeMode(mode)) return;
    if (!isUsedInMultipleEntities) {
      setIsLibraryModalOpen(true);
    }
  };

  const handleLibraryModalCancel = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setIsLibraryModalOpen(false);
  };

  const handleToggleLibrary = async (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    try {
      await updateSessionMutation({
        id: session.id,
        version: session.version,
        isInLibrary: !session.isInLibrary,
      });
      notification.notifySuccess(
        session.isInLibrary
          ? t('session.notification.removedFromLibrary', { title: session.title })
          : t('session.notification.addedToLibrary', { title: session.title }),
      );
      setIsLibraryModalOpen(false);
    } catch {
      notification.notifyError(t('session.notification.libraryError'));
    }
  };

  if (!multiFlowEvent) return null;

  const libraryButton = (
    <IconButton
      aria-label="add to library"
      onClick={handleLibraryIconClick}
      loading={isUpdatingSession}
      disabled={isUsedInMultipleEntities || isOrganizeMode(mode) || indicativeOnly}
      size="small"
      sx={{
        color: session.isInLibrary ? 'secondary.main' : 'inherit',
        width: '32px',
        height: '32px',
      }}
    >
      {session.isInLibrary ? <LibraryAddCheck fontSize="small" /> : <LibraryAddOutlined fontSize="small" />}
    </IconButton>
  );

  return (
    <>
      <Tooltip
        title={isUsedInMultipleEntities ? t('session.usedInFlowsTooltip') : ''}
        disableHoverListener={!isUsedInMultipleEntities}
      >
        <span style={{ display: 'inline-block' }}>{libraryButton}</span>
      </Tooltip>
      <ConfirmationModal
        open={isLibraryModalOpen}
        isLoading={isUpdatingSession}
        title={session.isInLibrary ? t('session.libraryModal.removeTitle') : t('session.libraryModal.addTitle')}
        text={
          session.isInLibrary
            ? t('session.libraryModal.removeText', { title: session.title })
            : t('session.libraryModal.addText', { title: session.title })
        }
        onConfirm={handleToggleLibrary}
        onCancel={handleLibraryModalCancel}
      />
    </>
  );
};
